import { GuideStep } from "../Guide";
import { LocatorScene } from "../art/locator";
import {
  FolderScene,
  FilterScene,
  ClustersScene,
  StatusScene,
  TableScene,
  SourceChoiceScene,
  ScanResultsScene,
} from "../art/scenes";
import { PropagateScene } from "views/components/LabelPropagation/guides/art/SeedScenes";

export const CREATE_DATASET_GUIDE_STEPS: GuideStep[] = [
  {
    title: "Choose where the data comes from",
    body: (
      <>
        <p>
          After <strong>New Dataset</strong> you choose where the AOI data comes
          from:
        </p>
        <ul>
          <li>
            <strong>Import from Scan Result</strong>: browse wafer scan results
            on the Camtek DataServer and pick the ones to import. Visual Layer
            exports them into folders for you.
          </li>
          <li>
            <strong>Upload folder</strong>: enter the name of a folder that
            already holds prepared AOI output.
          </li>
        </ul>
      </>
    ),
    tip: "Import from Scan Result is greyed out when DataServer is not configured, not enabled, or cannot be reached. Hover it to see which.",
    art: <SourceChoiceScene />,
  },
  {
    title: "Import from a scan result",
    body: (
      <>
        <p>
          Filter scan results by Job, Setup, Lot or scan date, then expand a
          scan result to see its wafers with their defect counts.
        </p>
        <ul>
          <li>
            Tick the wafers to import. All wafers in one import must come from
            the same scan result.
          </li>
          <li>
            <strong>Continue</strong> exports each selected scan result (one at
            a time) and fills the folder list for you, already validated.
          </li>
        </ul>
      </>
    ),
    tip: "If an export fails you get a message per scan result. The dataset is created only if at least one export succeeded.",
    art: <ScanResultsScene />,
  },
  {
    title: "Or enter your AOI folders",
    body: (
      <>
        <p>
          With <strong>Upload folder</strong>, type the name of each output
          folder (not the full path; the base path was set during installation)
          and click <strong>Add</strong>. Use + to add several at once.
        </p>
        <p>
          Every folder needs <strong>DB.csv</strong>,{" "}
          <strong>metadata.json</strong>, <strong>classes.json</strong> and an{" "}
          <strong>images</strong> folder. Visual Layer validates each one and
          shows the result in the Status column.
        </p>
      </>
    ),
    tip: "Hover an invalid status to see exactly what is wrong, for example a missing file or a filename that does not match DB.csv.",
    art: <FolderScene />,
  },
  {
    title: "Pick folders that belong together",
    body: (
      <>
        <p>
          All folders in one dataset must share the same Job, Setup, Recipe and
          class list. As soon as you tick the first folder, incompatible ones
          are greyed out. Untick it to start over.
        </p>
        <p>
          Use the Job, Setup and Recipe filters above the table to find the
          folders from one production run.
        </p>
      </>
    ),
    tip: "classes.json becomes the dataset's class list for good. Labels cannot be added or removed later.",
    art: <FilterScene />,
  },
  {
    title: "Preview and create",
    body: (
      <>
        <p>
          <strong>Continue</strong> shows a sample of images from the selected
          folders. Labels found in the OClass column of DB.csv appear as badges
          and are imported as User Labels.
        </p>
        <p>
          Click <strong>Create Dataset</strong>. Progress runs through
          Uploading, Initializing, Pre-Processing, Indexing and Saving.
        </p>
      </>
    ),
    tip: "You can leave this page. Follow progress in the Task Manager, or use Abort in the header to stop it.",
    art: <ClustersScene />,
  },
  {
    title: "When it is Ready",
    body: (
      <p>
        Large datasets take a while (about a couple of hours for 18,000 images).
        When the status turns Ready, open the dataset to explore the clusters
        and start Label Propagation. If creation fails, the dataset can only be
        deleted and recreated.
      </p>
    ),
    art: <TableScene />,
  },
];

export const ADD_MEDIA_GUIDE_STEPS: GuideStep[] = [
  {
    title: "Add folders to this dataset",
    body: (
      <>
        <p>
          This is the same wizard you used to create the dataset, but new
          folders are added to it instead of starting a new one. Folders already
          in the dataset are greyed out.
        </p>
        <p>
          A folder is accepted only if its Job, Setup, Recipe and class list
          match the dataset exactly. For anything else, create a new dataset.
        </p>
      </>
    ),
    art: <FolderScene />,
  },
  {
    title: "New media is visible right away",
    body: (
      <>
        <p>
          Click <strong>Add Media</strong>. Unlike creation, you can keep
          exploring while it runs. New images land in a{" "}
          <strong>New Media</strong> cluster and the dataset switches to{" "}
          <strong>Partial Index</strong>.
        </p>
        <p>
          While media is being added the dataset is Read Only and Label
          Propagation cannot start.
        </p>
      </>
    ),
    art: <StatusScene />,
  },
  {
    title: "Batch, then Re-Index",
    body: (
      <p>
        Add as many batches as you need, then click the status badge in the
        dataset header and choose <strong>Re-Index</strong> once. Re-indexing
        re-clusters all images and makes the new ones available to Label
        Propagation and training. Dataset and image IDs do not change.
      </p>
    ),
    tip: "If more data is on its way, wait and re-index everything together. Each Re-Index is a full run.",
    art: <LocatorScene target="status" label="Re-Index here" />,
  },
  {
    title: "Labeling the new images",
    body: (
      <>
        <p>
          After adding media the Labels section may show everything as
          unlabeled. This is a display limitation: earlier propagated labels are
          kept and can serve as seeds.
        </p>
        <p>
          Run Label Propagation again to label the new images. Because a new run
          replaces all previous propagated labels, first filter by each label
          and drag those images onto its seed card to keep them.
        </p>
      </>
    ),
    art: <PropagateScene />,
  },
];
